'use client';

import { useId, useMemo } from 'react';
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiBarChartBoxLine,
  RiCheckLine,
  RiFlowChart,
  RiPaletteLine,
  RiRobot2Line,
  RiShieldCheckLine,
  RiTeamLine,
} from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import { AGENT_DAILY_MESSAGE_LIMIT } from '@mentorivo/shared';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Planın içi" — /pricing sayfasının ikinci bölümü.
 *
 * ── Bölüm neyi düzeltiyor ─────────────────────────────────────────────────
 *
 * Fiyat kartları dört planın FARKINI gösteriyor (harcama, kredi, hesap, üye).
 * Kartlara bakan kullanıcının bir sonraki sorusu farkla ilgili değil:
 * "peki $19'a ne alıyorum, hangi modül hangi plana kilitli?"
 *
 * Cevap: hiçbiri. `limitsForTier` (packages/shared/plans.ts) plana göre
 * YALNIZCA dört sayı değiştiriyor; modül açıp kapatan bir kademe yok.
 * Bölüm bunu düz cümleyle söylüyor ve altına ürünün gerçekten yaptığı işi
 * seriyor.
 *
 * 🔴 Bu yüzden burada plana göre değişen TİK YOK — ne ✓/✗ matrisi, ne
 * "yalnızca Pro'da" rozeti. Öyle bir matris yazmak, kodda karşılığı olmayan
 * bir ürün politikası uydurmak olurdu. Plana bağlı tek ayrıntı ajanın günlük
 * mesaj hakkı ve o da `AGENT_DAILY_MESSAGE_LIMIT`ten okunuyor, elle
 * yazılmıyor.
 *
 * ── Karşılaştırma TABLOSU neden yok ───────────────────────────────────────
 *
 * Panelin içinde bir tane var (`dashboard/billing/plan-gate-catalog.tsx`) ve
 * orada doğru: kullanıcının planı yok, ekranın tamamı o karara ayrılmış.
 * Burada aynı beş satırı tekrarlamak fiyat kartlarının söylediğini ikinci kez
 * söylemek olurdu — kartlar zaten yan yana ve aynı dört satırı taşıyor.
 * Sayfaya bir tablo eklemek istenirse önce kartlardan ne çıkacağına karar
 * verilmeli; iki yüzey aynı sayıyı iki kez söylememeli.
 *
 * ── Metin uydurma değil ───────────────────────────────────────────────────
 *
 * Altı bloğun on sekiz satırı da üründe karşılığı olan davranışı anlatıyor ve
 * sitenin başka yerlerinde verilen sözle çelişmiyor:
 *  • Platform kapsamı Meta + Google Ads. TikTok `platforms` bölümünde
 *    "yakında" diyor; burada canlıymış gibi yazılmaz.
 *  • Atıf yalnızca TIKLAMA penceresi üzerinden — gösterim tabanlı model
 *    uygulanmıyor.
 *  • Ajan önerisi onaydan önce uygulanmıyor (`control` bölümünün onay modu).
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026 — sitenin bütün alt
 * bölümleri böyle): bölüm sayfa açılır açılmaz son hâlinde basılıyor
 * (`initial={false}`). Variant'lar yerinde, tetik tek satır.
 *
 * Sıra yine de tanımlı: hat çekiliyor → ikon → ad → tikler. Yani "kutu
 * çiziliyor, sonra doluyor" — `.tenet` ile aynı dil.
 *
 * Döngü, parallax ve otomatik oynayan hiçbir şey yok; kök layout'taki
 * `<MotionConfig reducedMotion="user">` bölümün tamamını karşılıyor.
 */

type Group = {
  key: string;
  icon: ReactNode;
  /** Satır anahtarları. Sıra ekranda göründüğü sıra. */
  items: readonly string[];
};

/* İkonlar kutu İÇİNDE değil (DESIGN.md §3) ve parıltı ailesi hiç yok.
   Sıra ürünün kendi sırası: dört modül (header'ın mega menüsündeki sırayla),
   sonra ekip, en sonda güvenlik — "kim kullanıyor" sorusu "nasıl korunuyor"
   sorusundan önce geliyor. */
const GROUPS: readonly Group[] = [
  {
    key: 'analyze',
    icon: <RiBarChartBoxLine aria-hidden="true" />,
    items: ['accounts', 'metrics', 'attribution'],
  },
  {
    key: 'automation',
    icon: <RiFlowChart aria-hidden="true" />,
    items: ['rules', 'actions', 'log'],
  },
  {
    key: 'agent',
    icon: <RiRobot2Line aria-hidden="true" />,
    items: ['ask', 'approve', 'quota'],
  },
  {
    key: 'creative',
    icon: <RiPaletteLine aria-hidden="true" />,
    items: ['generate', 'sizes', 'credits'],
  },
  {
    key: 'team',
    icon: <RiTeamLine aria-hidden="true" />,
    items: ['roles', 'alerts', 'bounds'],
  },
  {
    key: 'security',
    icon: <RiShieldCheckLine aria-hidden="true" />,
    items: ['oauth', 'vault', 'cut'],
  },
];

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
};

const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

/* Hat SOLDAN çekiliyor, ayraç YUKARIDAN iniyor — sayfanın geri kalanıyla aynı
   yön. İkisi de ayrı öğe, `border` değil: border animasyonlanamaz. */
const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: { duration: dur.slow, ease: ease.out } },
};

const edgeV: Variants = {
  hidden: { opacity: 0, scaleY: 0 },
  show: { opacity: 1, scaleY: 1, transition: { duration: dur.slow, ease: ease.out } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

/**
 * Ajanın günlük mesaj hakkı — plana göre değişen TEK ayrıntı.
 *
 * Rakamlar `AGENT_DAILY_MESSAGE_LIMIT`ten okunuyor: dört kademe tek tek
 * yazılsaydı sayfa, sınırı gerçekten uygulayan tablodan ayrışırdı.
 * `-1` sınırsız demek (Enterprise ve deneme) — aralığın iki ucu yalnızca
 * gerçek sayılardan çıkıyor. TRIAL plan değil, o yüzden aralığa girmiyor.
 */
function agentRange(): { min: number; max: number } {
  const counts = Object.entries(AGENT_DAILY_MESSAGE_LIMIT)
    .filter(([tier, value]) => tier !== 'TRIAL' && value > 0)
    .map(([, value]) => value);

  return { min: Math.min(...counts), max: Math.max(...counts) };
}

export function PricingIncluded() {
  const t = useTranslations('pricing.included');
  const locale = useLocale();
  const uid = useId();

  const nf = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }), [locale]);
  const agent = useMemo(agentRange, []);

  /* Yalnızca ajan satırı yer tutucu taşıyor; kalan on yedi satır düz cümle.
     Değerleri hepsine geçmek zararsız ve tek bir çağrı yolu bırakıyor. */
  const values = { min: nf.format(agent.min), max: nf.format(agent.max) };

  return (
    <section className="incl" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="incl-frame" variants={sectionV} initial={false} animate="show">
            <motion.div className="incl-head" variants={headV}>
              <p className="incl-eyebrow">{t('eyebrow')}</p>
              <h2 className="incl-title" id={`${uid}-title`}>
                {t.rich('title', { hl })}
              </h2>
              <p className="incl-lead">{t('lead')}</p>
            </motion.div>

            <motion.ul className="incl-grid" variants={gridV}>
              {GROUPS.map((group) => (
                <motion.li className="incl-cell" key={group.key} variants={cellV}>
                  <motion.span className="incl-rule" aria-hidden="true" variants={ruleV} />
                  {/* Dikey ayraç her hücrede basılıyor, hangisinde görüneceğine
                      CSS karar veriyor: iki sütunlu yerleşimde sağ sütun, üç
                      sütunluda ilk sütun dışındakiler. Kırılma noktasını JS'te
                      bilmek gerekmiyor. */}
                  <motion.span className="incl-edge" aria-hidden="true" variants={edgeV} />

                  <motion.span className="incl-icon" variants={itemV}>
                    {group.icon}
                  </motion.span>

                  <motion.h3 className="incl-name" variants={itemV}>
                    {t(`groups.${group.key}.name`)}
                  </motion.h3>

                  <ul className="incl-list">
                    {group.items.map((item) => (
                      <motion.li className="incl-item" key={item} variants={itemV}>
                        <RiCheckLine className="incl-check" size={18} aria-hidden="true" />
                        <span>{t(`groups.${group.key}.items.${item}`, values)}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
